import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Plus,
  Edit2,
  Trash2,
  Play,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  X,
  List,
  FileText,
  Warehouse,
  Save,
  Download,
  ArrowLeft,
} from 'lucide-react';
import { inspectionsApi, usersApi, processesApi } from '../api';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

const STATUS_LABELS = {
  scheduled: { label: 'Agendada', color: 'bg-blue-500/20 text-blue-400' },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-500/20 text-yellow-400' },
  completed: { label: 'Concluída', color: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Cancelada', color: 'bg-red-500/20 text-red-400' },
};

const RESULT_LABELS = {
  conforming: { label: 'Conforme', color: 'bg-green-500/20 text-green-400' },
  non_conforming: { label: 'Não Conforme', color: 'bg-red-500/20 text-red-400' },
  partial: { label: 'Parcial', color: 'bg-yellow-500/20 text-yellow-400' },
};

const Inspections = () => {
  const [activeTab, setActiveTab] = useState('inspections');
  const [inspections, setInspections] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [stats, setStats] = useState({ total_year: 0, completed: 0, scheduled: 0, avg_conformity_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [filters, setFilters] = useState({ status: '' });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [inspectionsRes, checklistsRes, statsRes] = await Promise.all([
        inspectionsApi.list(filters),
        inspectionsApi.listChecklists(),
        inspectionsApi.stats(),
      ]);
      setInspections(inspectionsRes.data);
      setChecklists(checklistsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreateInspection = async (data) => {
    try {
      await inspectionsApi.create(data);
      toast.success('Inspeção agendada com sucesso');
      setShowInspectionModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar inspeção'));
    }
  };

  const handleCreateChecklist = async (data) => {
    try {
      await inspectionsApi.createChecklist(data);
      toast.success('Checklist criado com sucesso');
      setShowChecklistModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar checklist'));
    }
  };

  const handleUpdateInspection = async (id, data) => {
    try {
      await inspectionsApi.update(id, data);
      toast.success('Inspeção atualizada');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteInspection = async (insp) => {
    try {
      await inspectionsApi.delete(insp.id);
      toast.success(`Inspeção ${insp.code} excluída com sucesso`);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir inspeção'));
    }
  };

  const tabs = [
    { id: 'inspections', label: 'Inspeções', icon: FileText },
    { id: 'checklists', label: 'Checklists', icon: List },
    { id: 'bpa', label: 'Roteiro de autoinspeção', icon: Warehouse },
  ];

  return (
    <div className="space-y-6" data-testid="inspections-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="text-green-500" />
            Auto Inspeção
          </h1>
          <p className="text-gray-400 mt-1">Roteiros de inspeção e verificação</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowChecklistModal(true)}
            className="btn-secondary"
            data-testid="add-checklist-btn"
          >
            <List size={20} />
            Novo Checklist
          </button>
          <button
            onClick={() => setShowInspectionModal(true)}
            className="btn-primary"
            data-testid="add-inspection-btn"
          >
            <Plus size={20} />
            Nova Inspeção
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total do Ano</p>
          <p className="text-2xl font-bold text-white">{stats.total_year}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Concluídas</p>
          <p className="text-2xl font-bold text-white">{stats.completed}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Agendadas</p>
          <p className="text-2xl font-bold text-white">{stats.scheduled}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Conformidade Média</p>
          <p className="text-2xl font-bold text-white">{stats.avg_conformity_rate}%</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-green-400 border-green-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'bpa' ? (
        <BPAForm checklists={checklists} users={users} />
      ) : (
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-green-500" size={32} />
            </div>
          ) : activeTab === 'inspections' ? (
            <InspectionsTable
              inspections={inspections}
              onUpdate={handleUpdateInspection}
              onDelete={handleDeleteInspection}
              onRefresh={loadData}
              filters={filters}
              setFilters={setFilters}
            />
          ) : (
            <ChecklistsTable checklists={checklists} />
          )}
        </div>
      )}

      {/* Inspection Modal */}
      {showInspectionModal && (
        <InspectionModal
          checklists={checklists}
          users={users}
          onClose={() => setShowInspectionModal(false)}
          onSave={handleCreateInspection}
        />
      )}

      {/* Checklist Modal */}
      {showChecklistModal && (
        <ChecklistModal
          onClose={() => setShowChecklistModal(false)}
          onSave={handleCreateChecklist}
        />
      )}
    </div>
  );
};

const InspectionsTable = ({ inspections, onUpdate, onDelete, onRefresh, filters, setFilters }) => {
  const [localSearch, setLocalSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [answering, setAnswering] = useState(null);
  const filtered = inspections.filter(i =>
    !localSearch || i.code?.toLowerCase().includes(localSearch.toLowerCase()) || i.checklist_name?.toLowerCase().includes(localSearch.toLowerCase())
  );
  return (
  <>
  <div className="space-y-4">
    {/* Filters */}
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar inspeções..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="input pl-10"
        />
      </div>
      <select
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="input px-3 w-full sm:w-60"
      >
        <option value="">Todos os Status</option>
        {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
    </div>

    {/* Table */}
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Checklist</th>
            <th>Área</th>
            <th>Data Agendada</th>
            <th>Inspetor</th>
            <th>Status</th>
            <th>Resultado</th>
            <th>Conformidade</th>
            <th className="text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center py-8 text-gray-500">Nenhuma inspeção encontrada</td>
            </tr>
          ) : (
            filtered.map((insp) => (
              <tr key={insp.id}>
                <td className="font-medium text-white font-mono">{insp.code}</td>
                <td className="text-white">{insp.checklist_name}</td>
                <td className="text-gray-400">{insp.area || '-'}</td>
                <td className="text-gray-400">{formatDateBR(insp.scheduled_date)}</td>
                <td className="text-gray-400">{insp.inspector_name || '-'}</td>
                <td>
                  <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[insp.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                    {STATUS_LABELS[insp.status]?.label || insp.status}
                  </span>
                </td>
                <td>
                  {insp.result && (
                    <span className={`px-2 py-1 rounded-full text-xs ${RESULT_LABELS[insp.result]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                      {RESULT_LABELS[insp.result]?.label || insp.result}
                    </span>
                  )}
                </td>
                <td>
                  {insp.conformity_rate !== null ? (
                    <span className={insp.conformity_rate >= 80 ? 'text-green-400' : insp.conformity_rate >= 60 ? 'text-yellow-400' : 'text-red-400'}>
                      {insp.conformity_rate.toFixed(1)}%
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    {insp.status === 'scheduled' && (
                      <button
                        onClick={() => onUpdate(insp.id, { status: 'in_progress', execution_date: new Date().toISOString().split('T')[0] })}
                        className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                        title="Iniciar"
                      >
                        <Play size={18} />
                      </button>
                    )}
                    {insp.status === 'in_progress' && (
                      <button
                        onClick={() => setAnswering(insp)}
                        className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                        title="Responder Checklist"
                      >
                        <CheckCircle size={18} />
                      </button>
                    )}
                    {insp.status === 'completed' && (
                      <button
                        onClick={() => setAnswering(insp)}
                        className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                        title="Revisar Checklist"
                      >
                        <Edit2 size={18} />
                      </button>
                    )}
                    {insp.status !== 'completed' && (
                      <button
                        onClick={() => setConfirmDelete(insp)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>

  {confirmDelete && (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-md">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-red-500/10 rounded-lg flex-shrink-0">
            <Trash2 className="text-red-400" size={24} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white mb-1">Excluir Inspeção</h2>
            <p className="text-gray-400 text-sm">
              Tem certeza que deseja excluir a inspeção{' '}
              <span className="text-white font-medium font-mono">{confirmDelete.code}</span>?
              Esta ação não poderá ser desfeita.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setConfirmDelete(null)}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            onClick={() => { onDelete(confirmDelete); setConfirmDelete(null); }}
            className="btn-danger"
          >
            <Trash2 size={16} />
            Excluir
          </button>
        </div>
      </div>
    </>
  )}

  {answering && (
    <InspectionAnswerModal
      inspection={answering}
      onClose={() => setAnswering(null)}
      onSaved={() => { setAnswering(null); onRefresh(); }}
    />
  )}
  </>
  );
};

// Formulário de respostas de uma inspeção específica (fora do Roteiro de Auto Inspeção).
// Reaproveita a mesma lógica de itens/conformidade usada no formulário BPA, para que a
// conformidade calculada seja sempre baseada nas respostas C/NC de fato registradas.
const InspectionAnswerModal = ({ inspection, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [answers, setAnswers] = useState({});
  const [header, setHeader] = useState({ auditores: '', participantes: '', data: new Date().toISOString().split('T')[0] });
  const [opportunities, setOpportunities] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingItems(true);
        const inspRes = await inspectionsApi.get(inspection.id);
        const insp = inspRes.data;
        const [itemsRes, resultsRes] = await Promise.all([
          inspectionsApi.getChecklistItems(insp.checklist_id),
          inspectionsApi.getResults(inspection.id),
        ]);
        setHeader({
          auditores: insp.inspector_name || '',
          participantes: insp.accompanied_by || '',
          data: insp.execution_date || insp.scheduled_date || new Date().toISOString().split('T')[0],
        });
        setOpportunities(insp.general_observations || '');
        setItems(itemsRes.data || []);
        const map = {};
        (resultsRes.data || []).forEach((r) => {
          map[r.checklist_item_id] = { result: r.result, observations: r.observations || '' };
        });
        setAnswers(map);
      } catch (error) {
        toast.error('Erro ao carregar formulário da inspeção');
      } finally {
        setLoadingItems(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection.id]);

  const setAnswer = (itemId, patch) => {
    setAnswers((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const counts = items.reduce(
    (acc, item) => {
      const r = answers[item.id]?.result;
      if (r === 'conforming') acc.c += 1;
      else if (r === 'non_conforming') acc.nc += 1;
      return acc;
    },
    { c: 0, nc: 0 }
  );

  const handleSave = async () => {
    const payload = Object.entries(answers)
      .filter(([, v]) => v?.result)
      .map(([checklist_item_id, v]) => ({ checklist_item_id, result: v.result, observations: v.observations || undefined }));
    if (payload.length === 0) {
      toast.error('Marque C ou NC em pelo menos um item');
      return;
    }
    setSaving(true);
    try {
      await inspectionsApi.saveResults(inspection.id, payload);
      const answered = payload.length === items.length;
      await inspectionsApi.update(inspection.id, {
        general_observations: opportunities,
        accompanied_by: header.participantes,
        execution_date: header.data,
        status: answered ? 'completed' : 'in_progress',
      });
      toast.success(answered ? 'Inspeção concluída com sucesso' : 'Progresso salvo');
      onSaved();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar formulário'));
    } finally {
      setSaving(false);
    }
  };

  const sections = [];
  items.forEach((item) => {
    const cat = item.category || 'Geral';
    let section = sections.find((s) => s.category === cat);
    if (!section) { section = { category: cat, items: [] }; sections.push(section); }
    section.items.push(item);
  });

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-0 max-w-4xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white">{inspection.code} — {inspection.checklist_name}</h2>
            <p className="text-gray-400 text-sm">Marque C/NC em cada item para calcular a conformidade da inspeção</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {loadingItems ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Este checklist não possui itens cadastrados.</div>
        ) : (
          <div className="max-h-[75vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Auditores</label>
                <input
                  type="text"
                  className="input w-full"
                  value={header.auditores}
                  onChange={(e) => setHeader({ ...header, auditores: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Participantes</label>
                <input
                  type="text"
                  className="input w-full"
                  value={header.participantes}
                  onChange={(e) => setHeader({ ...header, participantes: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Data</label>
                <input
                  type="date"
                  className="input w-full"
                  value={header.data}
                  onChange={(e) => setHeader({ ...header, data: e.target.value })}
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
            </div>

            {/* Desktop/tablet: tabela completa com rolagem horizontal se precisar */}
            <div className="table-container hidden sm:block">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-12">Nº</th>
                    <th>Item Verificado</th>
                    <th>Explicação do Item</th>
                    <th className="w-16 text-center">C</th>
                    <th className="w-16 text-center">NC</th>
                    <th className="w-64">Comentários</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section) => (
                    <React.Fragment key={section.category}>
                      <tr>
                        <td colSpan={6} className="bg-gray-700/50 text-white font-semibold text-sm py-2">
                          {section.category}
                        </td>
                      </tr>
                      {section.items.map((item) => {
                        const answer = answers[item.id] || {};
                        return (
                          <tr key={item.id}>
                            <td className="text-gray-400 align-top">{item.sequence}</td>
                            <td className="text-white align-top">
                              <div className="table-cell-wrap max-w-xs">
                                {item.question}
                                {item.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
                              </div>
                            </td>
                            <td className="text-gray-400 text-sm align-top">
                              <div className="table-cell-wrap max-w-xs">{item.criteria}</div>
                            </td>
                            <td className="text-center align-top">
                              <input
                                type="radio"
                                name={`result-${item.id}`}
                                checked={answer.result === 'conforming'}
                                onChange={() => setAnswer(item.id, { result: 'conforming' })}
                                className="accent-green-500 w-4 h-4"
                              />
                            </td>
                            <td className="text-center align-top">
                              <input
                                type="radio"
                                name={`result-${item.id}`}
                                checked={answer.result === 'non_conforming'}
                                onChange={() => setAnswer(item.id, { result: 'non_conforming' })}
                                className="accent-red-500 w-4 h-4"
                              />
                            </td>
                            <td className="align-top">
                              <input
                                type="text"
                                className="input w-full text-sm"
                                value={answer.observations || ''}
                                onChange={(e) => setAnswer(item.id, { observations: e.target.value })}
                                placeholder="Comentário..."
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Celular: cartões empilhados — evita ter que rolar de lado pra
                alcançar Conforme/Não Conforme, que numa tabela ficam fora
                da tela junto com as outras colunas. */}
            <div className="sm:hidden divide-y divide-gray-700">
              {sections.map((section) => (
                <div key={section.category}>
                  <div className="bg-gray-700/50 text-white font-semibold text-sm py-2 px-4">
                    {section.category}
                  </div>
                  {section.items.map((item) => {
                    const answer = answers[item.id] || {};
                    return (
                      <div key={item.id} className="p-4 space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 text-xs mt-0.5 shrink-0">{item.sequence}.</span>
                          <p className="text-white text-sm font-medium">
                            {item.question}
                            {item.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
                          </p>
                        </div>
                        {item.criteria && (
                          <p className="text-gray-400 text-xs pl-5">{item.criteria}</p>
                        )}
                        <div className="flex items-center gap-4 pl-5">
                          <label className="flex items-center gap-1.5 text-sm text-green-400">
                            <input
                              type="radio"
                              name={`result-mobile-${item.id}`}
                              checked={answer.result === 'conforming'}
                              onChange={() => setAnswer(item.id, { result: 'conforming' })}
                              className="accent-green-500 w-4 h-4"
                            />
                            Conforme
                          </label>
                          <label className="flex items-center gap-1.5 text-sm text-red-400">
                            <input
                              type="radio"
                              name={`result-mobile-${item.id}`}
                              checked={answer.result === 'non_conforming'}
                              onChange={() => setAnswer(item.id, { result: 'non_conforming' })}
                              className="accent-red-500 w-4 h-4"
                            />
                            Não Conforme
                          </label>
                        </div>
                        <input
                          type="text"
                          className="input w-full text-sm ml-5"
                          style={{ width: 'calc(100% - 1.25rem)' }}
                          value={answer.observations || ''}
                          onChange={(e) => setAnswer(item.id, { observations: e.target.value })}
                          placeholder="Comentário..."
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-gray-700 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-6">
                <span className="text-gray-400 text-sm">Resultado</span>
                <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">C: {counts.c}</span>
                <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-medium">NC: {counts.nc}</span>
              </div>
              <span className="text-sm text-gray-400">
                {counts.c + counts.nc} de {items.length} itens respondidos
                {counts.c + counts.nc < items.length && ' — responda todos para concluir a inspeção'}
              </span>
            </div>

            <div className="p-4 border-t border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-2">Oportunidades Identificadas</label>
              <textarea
                className="input w-full"
                rows={3}
                value={opportunities}
                onChange={(e) => setOpportunities(e.target.value)}
                placeholder="Descreva as oportunidades de melhoria identificadas durante a auditoria..."
              />
            </div>

            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
              <button onClick={handleSave} className="btn-primary flex items-center gap-2" disabled={saving}>
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Salvar Formulário
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ChecklistsTable = ({ checklists }) => (
  <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Código</th>
          <th>Nome</th>
          <th>Área</th>
          <th>Processo</th>
          <th>Frequência</th>
          <th>Itens</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {checklists.length === 0 ? (
          <tr>
            <td colSpan="7" className="text-center py-8 text-gray-500">Nenhum checklist cadastrado</td>
          </tr>
        ) : (
          checklists.map((cl) => (
            <tr key={cl.id}>
              <td className="font-medium text-white font-mono">{cl.code}</td>
              <td className="text-white">{cl.name}</td>
              <td className="text-gray-400">{cl.area || '-'}</td>
              <td className="text-gray-400">{cl.process || '-'}</td>
              <td className="text-gray-400 capitalize">{cl.frequency || '-'}</td>
              <td className="text-gray-400">{cl.items_count || 0}</td>
              <td>
                <span className={`px-2 py-1 rounded-full text-xs ${cl.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {cl.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const InspectionModal = ({ checklists, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    checklist_id: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    area: '',
    location: '',
    inspector_id: '',
    accompanied_by: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.checklist_id) {
      toast.error('Selecione um checklist');
      return;
    }
    const dateError = validateDateFields([
      { value: formData.scheduled_date, label: 'Data Agendada' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.inspector_id) delete data.inspector_id;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Nova Inspeção</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Checklist *</label>
            <select
              value={formData.checklist_id}
              onChange={(e) => setFormData({ ...formData, checklist_id: e.target.value })}
              className="input w-full"
              required
            >
              <option value="">Selecione um checklist...</option>
              {checklists.filter(c => c.is_active).map((cl) => (
                <option key={cl.id} value={cl.id}>{cl.code} - {cl.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data Agendada</label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Inspetor</label>
              <select
                value={formData.inspector_id}
                onChange={(e) => setFormData({ ...formData, inspector_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
            <input
              type="text"
              value={formData.area}
              onChange={(e) => setFormData({ ...formData, area: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Local</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Acompanhado por</label>
            <input
              type="text"
              value={formData.accompanied_by}
              onChange={(e) => setFormData({ ...formData, accompanied_by: e.target.value })}
              className="input w-full"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Agendar Inspeção'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const ChecklistModal = ({ onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    area: '',
    process: '',
    frequency: 'monthly',
    checklist_type: 'inspecao',
    items: [],
  });
  const [newItem, setNewItem] = useState({ sequence: 1, question: '', criteria: '', is_critical: false });

  useEffect(() => {
    const loadProcesses = async () => {
      try {
        const response = await processesApi.listActive();
        setProcesses(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar processos:', error);
      }
    };
    loadProcesses();
  }, []);

  const addItem = () => {
    if (!newItem.question) return;
    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem, sequence: formData.items.length + 1 }],
    });
    setNewItem({ sequence: formData.items.length + 2, question: '', criteria: '', is_critical: false });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index).map((item, i) => ({ ...item, sequence: i + 1 }));
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Adicione pelo menos um item ao checklist');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Novo Checklist</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '') })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
              <input
                type="text"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Processo</label>
              <select
                value={formData.process}
                onChange={(e) => setFormData({ ...formData, process: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione um processo</option>
                {processes.map(p => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Frequência</label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="input w-full"
              >
                <option value="daily">Diária</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
                <option value="quarterly">Trimestral</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Checklist</label>
              <select
                value={formData.checklist_type}
                onChange={(e) => setFormData({ ...formData, checklist_type: e.target.value })}
                className="input w-full"
              >
                <option value="inspecao">Inspeção</option>
                <option value="auditoria">Auditoria</option>
                <option value="manutencao">Manutenção</option>
                <option value="seguranca">Segurança</option>
                <option value="qualidade">Qualidade</option>
                <option value="5s">5S</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
          </div>

          {/* Items */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-white font-medium mb-3">Itens do Checklist</h4>

            {/* Add Item Form */}
            <div className="flex gap-2 items-end mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={newItem.question}
                  onChange={(e) => setNewItem({ ...newItem, question: e.target.value })}
                  className="input w-full"
                  placeholder="Pergunta/Item de verificação"
                />
              </div>
              <label className="flex items-center gap-1 text-gray-400 text-sm">
                <input
                  type="checkbox"
                  checked={newItem.is_critical}
                  onChange={(e) => setNewItem({ ...newItem, is_critical: e.target.checked })}
                  className="form-checkbox text-red-500"
                />
                Crítico
              </label>
              <button type="button" onClick={addItem} className="btn-secondary px-3">
                <Plus size={18} />
              </button>
            </div>

            {/* Items List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {formData.items.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700/50 rounded">
                  <span className="text-gray-400 text-sm w-6">{item.sequence}.</span>
                  <span className="flex-1 text-white text-sm">{item.question}</span>
                  {item.is_critical && <span className="text-xs text-red-400">CRÍTICO</span>}
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              {formData.items.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">Nenhum item adicionado</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Criar Checklist'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// Lista de auditorias/inspeções já realizadas (de qualquer checklist), com ações
// para visualizar (retomar o preenchimento) ou baixar o PDF do formulário.
const BPAAuditList = ({ inspections, onSelect }) => {
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (insp) => {
    setDownloadingId(insp.id);
    try {
      const res = await inspectionsApi.downloadReportPdf(insp.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${insp.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar PDF da auditoria');
    } finally {
      setDownloadingId(null);
    }
  };

  if (inspections.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        Nenhuma auditoria realizada ainda. Clique em "Nova" para começar a preencher o formulário.
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Checklist</th>
            <th>Inspetor</th>
            <th>Data</th>
            <th>Status</th>
            <th>Resultado</th>
            <th>Conformidade</th>
            <th className="text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {inspections.map((insp) => (
            <tr key={insp.id}>
              <td className="font-medium text-white font-mono">{insp.code}</td>
              <td className="text-white">{insp.checklist_name || '-'}</td>
              <td className="text-gray-400">{insp.inspector_name || '-'}</td>
              <td className="text-gray-400">{formatDateBR(insp.execution_date || insp.scheduled_date)}</td>
              <td>
                <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[insp.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                  {STATUS_LABELS[insp.status]?.label || insp.status}
                </span>
              </td>
              <td>
                {insp.result && (
                  <span className={`px-2 py-1 rounded-full text-xs ${RESULT_LABELS[insp.result]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                    {RESULT_LABELS[insp.result]?.label || insp.result}
                  </span>
                )}
              </td>
              <td>
                {insp.conformity_rate !== null && insp.conformity_rate !== undefined ? (
                  <span className={insp.conformity_rate >= 80 ? 'text-green-400' : insp.conformity_rate >= 60 ? 'text-yellow-400' : 'text-red-400'}>
                    {insp.conformity_rate.toFixed(1)}%
                  </span>
                ) : '-'}
              </td>
              <td>
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => onSelect(insp.id)}
                    className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                    title="Visualizar"
                  >
                    <FileText size={18} />
                  </button>
                  <button
                    onClick={() => handleDownload(insp)}
                    className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg disabled:opacity-50"
                    title="Baixar PDF"
                    disabled={downloadingId === insp.id}
                  >
                    {downloadingId === insp.id ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Aba "Boas Práticas de Armazenagem" — reproduz o formulário de auditoria da planilha
// (cabeçalho auditores/participantes/data, itens agrupados por seção com colunas C/NC e
// comentários, resultado consolidado e oportunidades identificadas).
const BPAForm = ({ checklists, users }) => {
  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [newChecklistId, setNewChecklistId] = useState('');
  const [answers, setAnswers] = useState({}); // { [checklist_item_id]: { result, observations } }
  const [header, setHeader] = useState({ auditores: '', participantes: '', data: new Date().toISOString().split('T')[0] });
  const [opportunities, setOpportunities] = useState('');
  const [loadingResults, setLoadingResults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const activeChecklists = checklists.filter((c) => c.is_active);

  const emptyHeader = () => ({ auditores: '', participantes: '', data: new Date().toISOString().split('T')[0] });

  const loadInspections = async () => {
    const inspRes = await inspectionsApi.list({});
    setInspections(inspRes.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingList(true);
        await loadInspections();
      } catch (error) {
        toast.error('Erro ao carregar inspeções');
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setChecklist(null);
      setItems([]);
      setAnswers({});
      setHeader(emptyHeader());
      setOpportunities('');
      return;
    }
    (async () => {
      try {
        setLoadingResults(true);
        const inspRes = await inspectionsApi.get(selectedId);
        const insp = inspRes.data;
        const [checklistRes, itemsRes, resultsRes] = await Promise.all([
          inspectionsApi.getChecklist(insp.checklist_id),
          inspectionsApi.getChecklistItems(insp.checklist_id),
          inspectionsApi.getResults(selectedId),
        ]);
        setChecklist(checklistRes.data);
        setItems(itemsRes.data || []);
        setHeader({
          auditores: insp.inspector_name || '',
          participantes: insp.accompanied_by || '',
          data: insp.execution_date || insp.scheduled_date || new Date().toISOString().split('T')[0],
        });
        setOpportunities(insp.general_observations || '');

        const map = {};
        (resultsRes.data || []).forEach((r) => {
          map[r.checklist_item_id] = { result: r.result, observations: r.observations || '' };
        });
        setAnswers(map);
      } catch (error) {
        toast.error('Erro ao carregar dados da auditoria');
      } finally {
        setLoadingResults(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleNewAuditoria = async () => {
    if (!newChecklistId) {
      toast.error('Selecione um checklist para a nova auditoria');
      return;
    }
    setCreating(true);
    try {
      const res = await inspectionsApi.create({ checklist_id: newChecklistId, scheduled_date: header.data, accompanied_by: header.participantes });
      toast.success(`Auditoria ${res.data.code} criada`);
      await loadInspections();
      setSelectedId(res.data.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar auditoria'));
    } finally {
      setCreating(false);
    }
  };

  const setAnswer = (itemId, patch) => {
    setAnswers((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const counts = items.reduce(
    (acc, item) => {
      const r = answers[item.id]?.result;
      if (r === 'conforming') acc.c += 1;
      else if (r === 'non_conforming') acc.nc += 1;
      return acc;
    },
    { c: 0, nc: 0 }
  );

  const handleSave = async () => {
    if (!selectedId) {
      toast.error('Crie ou selecione uma auditoria antes de salvar');
      return;
    }
    const payload = Object.entries(answers)
      .filter(([, v]) => v?.result)
      .map(([checklist_item_id, v]) => ({ checklist_item_id, result: v.result, observations: v.observations || undefined }));
    if (payload.length === 0) {
      toast.error('Marque C ou NC em pelo menos um item');
      return;
    }
    setSaving(true);
    try {
      await inspectionsApi.saveResults(selectedId, payload);
      const answered = payload.length === items.length;
      await inspectionsApi.update(selectedId, {
        general_observations: opportunities,
        accompanied_by: header.participantes,
        execution_date: header.data,
        status: answered ? 'completed' : 'in_progress',
      });
      toast.success('Formulário salvo com sucesso');
      await loadInspections();
      setSelectedId('');
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar formulário'));
    } finally {
      setSaving(false);
    }
  };

  if (loadingList) {
    return (
      <div className="card p-12 flex items-center justify-center">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  // Agrupa mantendo a ordem de sequência das seções (categorias) do formulário
  const sections = [];
  items.forEach((item) => {
    const cat = item.category || 'Geral';
    let section = sections.find((s) => s.category === cat);
    if (!section) { section = { category: cat, items: [] }; sections.push(section); }
    section.items.push(item);
  });

  return (
    <div className="card overflow-hidden">
      {/* Cabeçalho */}
      <div className="p-4 border-b border-gray-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Auditoria</label>
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="input w-full">
            <option value="">Selecione uma auditoria...</option>
            {inspections.map((i) => (
              <option key={i.id} value={i.id}>{i.code} — {i.checklist_name} — {formatDateBR(i.scheduled_date)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Auditores</label>
          <input type="text" className="input w-full" value={header.auditores} onChange={(e) => setHeader({ ...header, auditores: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Participantes</label>
          <input type="text" className="input w-full" value={header.participantes} onChange={(e) => setHeader({ ...header, participantes: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Data</label>
          <input type="date" className="input w-full" value={header.data} onChange={(e) => setHeader({ ...header, data: e.target.value })} min="1900-01-01" max="2099-12-31" />
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-400 mb-1">Checklist (nova)</label>
            <select value={newChecklistId} onChange={(e) => setNewChecklistId(e.target.value)} className="input w-full">
              <option value="">Selecione...</option>
              {activeChecklists.map((cl) => (
                <option key={cl.id} value={cl.id}>{cl.name}</option>
              ))}
            </select>
          </div>
          <button onClick={handleNewAuditoria} className="btn-primary flex items-center gap-2 whitespace-nowrap" disabled={creating}>
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Nova
          </button>
        </div>
      </div>

      {!selectedId ? (
        <BPAAuditList inspections={inspections} onSelect={setSelectedId} />
      ) : loadingResults ? (
        <div className="p-12 flex items-center justify-center">
          <Loader2 className="animate-spin text-green-500" size={28} />
        </div>
      ) : (
        <>
          {checklist && (
            <div className="px-4 pt-4 text-sm text-gray-400">
              Checklist: <span className="text-white font-medium">{checklist.name}</span>
            </div>
          )}
          {/* Desktop/tablet: tabela completa com rolagem horizontal se precisar */}
          <div className="table-container hidden sm:block">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-12">Nº</th>
                  <th>Item Verificado</th>
                  <th>Explicação do Item</th>
                  <th className="w-16 text-center">C</th>
                  <th className="w-16 text-center">NC</th>
                  <th className="w-64">Comentários</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <React.Fragment key={section.category}>
                    <tr>
                      <td colSpan={6} className="bg-gray-700/50 text-white font-semibold text-sm py-2">
                        {section.category}
                      </td>
                    </tr>
                    {section.items.map((item) => {
                      const answer = answers[item.id] || {};
                      return (
                        <tr key={item.id}>
                          <td className="text-gray-400 align-top">{item.sequence}</td>
                          <td className="text-white align-top">
                            <div className="table-cell-wrap max-w-xs">
                              {item.question}
                              {item.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
                            </div>
                          </td>
                          <td className="text-gray-400 text-sm align-top">
                            <div className="table-cell-wrap max-w-xs">{item.criteria}</div>
                          </td>
                          <td className="text-center align-top">
                            <input
                              type="radio"
                              name={`result-${item.id}`}
                              checked={answer.result === 'conforming'}
                              onChange={() => setAnswer(item.id, { result: 'conforming' })}
                              className="accent-green-500 w-4 h-4"
                            />
                          </td>
                          <td className="text-center align-top">
                            <input
                              type="radio"
                              name={`result-${item.id}`}
                              checked={answer.result === 'non_conforming'}
                              onChange={() => setAnswer(item.id, { result: 'non_conforming' })}
                              className="accent-red-500 w-4 h-4"
                            />
                          </td>
                          <td className="align-top">
                            <input
                              type="text"
                              className="input w-full text-sm"
                              value={answer.observations || ''}
                              onChange={(e) => setAnswer(item.id, { observations: e.target.value })}
                              placeholder="Comentário..."
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Celular: cartões empilhados — evita rolagem lateral pra alcançar
              Conforme/Não Conforme, que numa tabela ficam fora da tela. */}
          <div className="sm:hidden divide-y divide-gray-700">
            {sections.map((section) => (
              <div key={section.category}>
                <div className="bg-gray-700/50 text-white font-semibold text-sm py-2 px-4">
                  {section.category}
                </div>
                {section.items.map((item) => {
                  const answer = answers[item.id] || {};
                  return (
                    <div key={item.id} className="p-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-gray-500 text-xs mt-0.5 shrink-0">{item.sequence}.</span>
                        <p className="text-white text-sm font-medium">
                          {item.question}
                          {item.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
                        </p>
                      </div>
                      {item.criteria && (
                        <p className="text-gray-400 text-xs pl-5">{item.criteria}</p>
                      )}
                      <div className="flex items-center gap-4 pl-5">
                        <label className="flex items-center gap-1.5 text-sm text-green-400">
                          <input
                            type="radio"
                            name={`result-mobile-${item.id}`}
                            checked={answer.result === 'conforming'}
                            onChange={() => setAnswer(item.id, { result: 'conforming' })}
                            className="accent-green-500 w-4 h-4"
                          />
                          Conforme
                        </label>
                        <label className="flex items-center gap-1.5 text-sm text-red-400">
                          <input
                            type="radio"
                            name={`result-mobile-${item.id}`}
                            checked={answer.result === 'non_conforming'}
                            onChange={() => setAnswer(item.id, { result: 'non_conforming' })}
                            className="accent-red-500 w-4 h-4"
                          />
                          Não Conforme
                        </label>
                      </div>
                      <input
                        type="text"
                        className="input w-full text-sm ml-5"
                        style={{ width: 'calc(100% - 1.25rem)' }}
                        value={answer.observations || ''}
                        onChange={(e) => setAnswer(item.id, { observations: e.target.value })}
                        placeholder="Comentário..."
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Resultado */}
          <div className="p-4 border-t border-gray-700 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-6">
              <span className="text-gray-400 text-sm">Resultado</span>
              <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">C: {counts.c}</span>
              <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-medium">NC: {counts.nc}</span>
            </div>
            <span className="text-sm text-gray-400">
              {counts.c + counts.nc} de {items.length} itens respondidos
              {counts.c + counts.nc < items.length && ' — responda todos para concluir a inspeção'}
            </span>
          </div>

          {/* Oportunidades Identificadas */}
          <div className="p-4 border-t border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">Oportunidades Identificadas</label>
            <textarea
              className="input w-full"
              rows={3}
              value={opportunities}
              onChange={(e) => setOpportunities(e.target.value)}
              placeholder="Descreva as oportunidades de melhoria identificadas durante a auditoria..."
            />
          </div>

          <div className="p-4 border-t border-gray-700 flex justify-between">
            <button onClick={() => setSelectedId('')} className="btn-secondary flex items-center gap-2" disabled={saving}>
              <ArrowLeft size={18} />
              Voltar
            </button>
            <button onClick={handleSave} className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Salvar Formulário
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Inspections;
